<template>
  <v-card v-if="items">
    <v-card-title>
      <span class="table-card-title">Resultado por produto</span>
      <v-spacer />
      <export-data :data-fetcher="getDataToExport" filename="resultados_por_produto" ghost />
    </v-card-title>
    <v-data-table
      v-model="selectedInternalItems"
      :items="items"
      :loading="loading"
      :headers="headers"
      hide-default-footer
      disable-pagination
      no-data-text="Não há dados nesta campanha"
    >
      <template v-slot:[`header.impact`]="{ header }">
        <mf-tooltip top max-width="192px">
          <template v-slot:activator="{ on }">
            {{ header.text }}
            <mf-icon small color="grey" v-on="on">info</mf-icon>
          </template>
          <span>Impacto desta campanha na quantidade deste produto que foi vendida no período</span>
        </mf-tooltip>
      </template>
      <template v-slot:[`item.product.sku`]="{ item }">
        <router-link class="link" target="_blank" :to="`/produtos/detalhes-produto/${item.product.sku}`">{{ item.product.sku }}</router-link>
      </template>
      <template v-slot:[`item.product.name`]="{ item }">
        {{ item.product.name }}
      </template>
      <template v-slot:[`item.clients`]="{ item }">
        {{ item.clients }}
        <mf-quick-actions v-bind="productQuickActionsProps(item)" />
      </template>
      <template v-slot:[`item.quantity`]="{ item }">
        {{ item.quantity }}
      </template>
      <template v-slot:[`item.revenue`]="{ item }">
        {{ setCurrencyValue(item.revenue) }}
      </template>
      <template v-slot:[`item.averageTicket`]="{ item }">
        {{ setCurrencyValue(item.averageTicket) }}
      </template>
      <template v-slot:[`item.impact`]="{ item }">
        {{ setPercentageValue(localeString(item.impact)) }}
      </template>
      <template v-if="getSafe(() => items.length)" v-slot:[`body.append`]="{ headers, items }">
        <tr style="background-color: #f7f7f7">
          <td v-for="(header, i) in headers" :key="i">
            <div v-if="header.value === 'product.sku'">
              <mf-tooltip top max-width="192px">
                <template v-slot:activator="{ on }">
                  <div class="d-flex align-center">
                    <span>Total:</span>
                    <mf-icon class="ml-1" small color="cinza-0" v-on="on"> info </mf-icon>
                  </div>
                </template>
                <span>
                  O total é calculado considerando clientes e cupons fiscais únicos. Um mesmo cliente pode comprar diferentes produtos e um mesmo produto pode
                  aparecer em diversos cupons fiscais. Por isso, a soma dos valores da tabela pode não ser igual ao exibido na linha “Total”.
                </span>
              </mf-tooltip>
            </div>

            <div v-if="header.value === 'product.name'"></div>

            <div v-if="header.value === 'clients'">
              {{ itemsTotal.clients | abreviation({ isInteger: true }) }}
              <mf-quick-actions v-bind="totalQuickActionsProps()" />
            </div>

            <div v-if="header.value === 'tickets'">
              {{ itemsTotal.tickets | abreviation({ isInteger: true }) }}
            </div>

            <div v-if="header.value === 'revenue'">
              {{ setCurrencyValue(itemsTotal.revenue) }}
            </div>

            <div v-if="header.value === 'averageTicket'">
              {{ setCurrencyValue(itemsTotal.averageTicket) }}
            </div>

            <div v-if="header.value === 'impact'">
              {{ setPercentageValue(localeString(itemsTotal.impact)) }}
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { putEmptyValue, localeString } from '@/config/helpers.js'
import ExportData from '@/components/export-data/ExportData.vue'
import { couponDiscount } from '../../../mixins/coupon-discount'
import { criteriaGroup, consumedCouponsCriteria, totalConsumedCouponsCriteria } from '@/components/quick-actions/segmentationGroups.js'
import { DateTime } from 'luxon'
export default {
  name: 'ResultsProductsTable',
  components: {
    ExportData,
  },
  mixins: [couponDiscount],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    selectedInternalItems: [],
  }),
  computed: {
    headers() {
      const headers = [
        {
          text: 'Código',
          value: 'product.sku',
          width: '5%',
        },
        {
          text: 'Nome',
          value: 'product.name',
          width: '20%',
        },
        {
          text: 'Clientes que compraram',
          value: 'clients',
          width: '20%',
        },
        {
          text: 'Cupons fiscais',
          value: 'tickets',
          width: '10%',
        },
        {
          text: 'Faturamento',
          value: 'revenue',
          width: '15%',
        },
        {
          text: 'Ticket médio',
          value: 'averageTicket',
          width: '15%',
        },
        {
          text: 'Impacto',
          value: 'impact',
          width: '15%',
        },
      ]
      return headers
    },
    items() {
      return this.metrics
        ? this.metrics?.productsMetrics?.table.map((metric) => {
            return {
              product: {
                name: metric?.product.name,
                sku: metric?.product.sku,
              },
              clients: this.putEmptyValue(metric?.clients, 0),
              revenue: this.putEmptyValue(metric?.revenue, 0),
              tickets: this.putEmptyValue(metric?.tickets, 0),
              averageTicket: this.putEmptyValue(metric?.averageTicket, 0),
              impact: this.putEmptyValue(metric?.impact, 0),
            }
          })
        : []
    },
    itemsTotal() {
      const totalsMetrics = this.metrics?.productsMetrics?.totals
      return this.metrics
        ? {
            clients: this.putEmptyValue(totalsMetrics?.clients, 0),
            revenue: this.putEmptyValue(totalsMetrics?.revenue, 0),
            tickets: this.putEmptyValue(totalsMetrics?.tickets, 0),
            averageTicket: this.putEmptyValue(totalsMetrics?.averageTicket, 0),
            // impact: this.putEmptyValue((this.metrics?.totals?.received?.quantity / this.metrics?.totals?.total?.quantity) * 100, 0),
            impact: this.putEmptyValue(totalsMetrics?.impact, 0),
          }
        : []
    },
  },
  methods: {
    localeString,
    putEmptyValue,
    productQuickActionsProps(item) {
      const startDate = DateTime.fromFormat(this.benefit.startDate.slice(0, 10), 'yyyy-MM-dd').toFormat('yyyyMMdd')
      const expireDate = DateTime.fromFormat(this.benefit.expiresAt.slice(0, 10), 'yyyy-MM-dd').toFormat('yyyyMMdd')
      // ARRUMAR OS GRUPOS DEPOIS
      const unitsConfiguration = this.benefit.unitsInfo.map((unit) => {
        return { value: unit.id_erp, dataType: ['PHYSICAL', 'ECOMMERCE'] }
      })
      return {
        key: `clients-consumed-coupon`,
        quickActionGroupId: `clients-consumed-coupon`,
        shortDescription: 'Clientes que consumiram o Cupom de desconto',
        longDescription: 'Clientes que consumiram o Cupom de desconto',
        segmentationGroup: () =>
          criteriaGroup([
            consumedCouponsCriteria(
              [this.benefit._id],
              unitsConfiguration,
              [item.product.sku],
              this.benefit.variableValidity ? 0 : 1,
              [startDate, expireDate],
              true
            ),
          ]),
      }
    },
    totalQuickActionsProps() {
      const startDate = DateTime.fromFormat(this.benefit.startDate.slice(0, 10), 'yyyy-MM-dd').toFormat('yyyyMMdd')
      const expireDate = DateTime.fromFormat(this.benefit.expiresAt.slice(0, 10), 'yyyy-MM-dd').toFormat('yyyyMMdd')
      const products = this.metrics?.productsMetrics?.table.map((metric) => metric.product.sku)
      const unitsConfiguration = this.benefit.unitsInfo.map((unit) => {
        return { value: unit.id_erp, dataType: ['PHYSICAL', 'ECOMMERCE'] }
      })
      return {
        key: `clients-consumed-coupon`,
        quickActionGroupId: `clients-consumed-coupon`,
        shortDescription: 'Clientes que consumiram o Cupom de desconto',
        longDescription: 'Clientes que consumiram o Cupom de desconto',
        segmentationGroup: () => criteriaGroup([totalConsumedCouponsCriteria([this.benefit._id], unitsConfiguration, products, [startDate, expireDate])]),
      }
    },
    setCurrencyValue(value) {
      return `R$ ${this.localeString(value, 2)}`
    },
    setPercentageValue(value) {
      return `${value}%`
    },
    getDataToExport() {
      const headers = this.headers.map((header) => header.text)
      const rows = this.items.map((item) =>
        this.headers.map((header) => {
          if (header.value.includes('.')) {
            const attrs = header.value.split('.')
            const rowType = typeof item[attrs[0]][attrs[1]]
            return rowType === 'number' ? this.localeString(item[attrs[0]][attrs[1]], 2) : item[attrs[0]][attrs[1]]
          }
          const rowType = typeof item[header.value]

          return rowType === 'number' ? this.localeString(item[header.value], 2) : item[header.value]
        })
      )
      return { headers, rows }
    },
  },
}
</script>
